import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import {
   Button,
   Hierarchy,
   IconButton,
   IconButtonHierarchy,
   IconButtonSubVariant,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import {
   ArrowNarrowLeftIcon,
   ChevronLeftDoubleIcon,
   ClockFastBackwardIcon,
   MessageTextSquare02Icon,
   PlusIcon,
   RefreshCCW01Icon,
   SearchMdIcon,
   XCloseIcon
} from '@flowwai-frontend/design-system/icons'
import {
   ChatConfigUIStateEnum,
   ThreadModel,
   useAIChatUIStore
} from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   InfiniteScrollList,
   Loader,
   useCustomT
} from '@flowwai-frontend/shared/common-components'
import { isMobileDevice } from '@flowwai-frontend/shared/utils/web'

import './index.css'
import * as Styles from './styles'

interface Props {
   onClose: () => void
   onClickNewThread: () => void
   threads: ThreadModel[]
   hasMoreThreads: boolean
   onClickChatHistory: (threadId: string) => void
   fetchMoreThreads: () => void
   shouldShowFetchMoreErrorView: boolean
   searchText: string
   setSearchText: (searchText: string) => void
   onClickBack: () => void
   isChatBoxExpanded: boolean
   setShouldShowChatHistoryInSidepeek: (value: boolean) => void
}

const CHAT_HISTORY_LIST_SCROLL_TARGET_ID = 'chat-history-list-scroll-target-id'

const ChatHistoryController = (props: Props) => {
   const {
      onClose,
      threads,
      hasMoreThreads,
      fetchMoreThreads,
      shouldShowFetchMoreErrorView,
      onClickChatHistory,
      searchText,
      setSearchText,
      onClickBack,
      isChatBoxExpanded,
      setShouldShowChatHistoryInSidepeek
   } = props

   const { theme } = useAppTheme()
   const themes = theme.aiChatSupport.aiActionCenter

   const aiChatUIStore = useAIChatUIStore()
   const t = useCustomT('aiChatSupport:common')
   const chatBoxT = useCustomT('aiChatSupport:chatBox')

   const onClickNewThread = () => {
      props.onClickNewThread()
      aiChatUIStore.setActiveUIStateEnum(ChatConfigUIStateEnum.SearchState)
   }

   const renderErrorView = (): React.ReactElement | null =>
      shouldShowFetchMoreErrorView ? (
         <div className='flex justify-center'>
            <Button
               size={'Small'}
               subVariant={SubVariant.GrayOutline}
               hierarchy={Hierarchy.Secondary}
               leftIcon={() => <RefreshCCW01Icon />}
               onClick={fetchMoreThreads}
            >
               {t('retry')}
            </Button>
         </div>
      ) : null

   const renderThreadCard = (thread: ThreadModel): React.ReactElement => (
      <div
         className={Styles.threadCardContainerClassName}
         onClick={() => {
            onClickChatHistory(thread.threadId.toString())
         }}
      >
         <div className='shrink-0'>
            <MessageTextSquare02Icon />
         </div>
         <p
            className={cn(
               Styles.threadNameTextClassName,
               themes.chatHistoryCardTextColor
            )}
            title={thread.name}
         >
            {thread.name}
         </p>
      </div>
   )

   const renderLoadingView = (): React.ReactElement => (
      <div className='flex justify-center h-36px'>
         <Loader />
      </div>
   )

   const renderChatHistoryList = (): React.ReactElement => (
      <InfiniteScrollList
         dataLength={threads.length}
         hasMore={hasMoreThreads}
         next={fetchMoreThreads}
         scrollThreshold={0.4}
         loader={renderLoadingView()}
         scrollableTarget={CHAT_HISTORY_LIST_SCROLL_TARGET_ID}
      >
         <div className={'flex flex-col gap-y-2'}>
            {threads.map(thread => (
               <div key={thread.threadId}>{renderThreadCard(thread)}</div>
            ))}
         </div>
         {renderErrorView()}
      </InfiniteScrollList>
   )

   const renderChatHistoryHeading = (): React.ReactElement => (
      <div
         className={cn(Styles.headingContainerClassName, {
            '!mb-0': isChatBoxExpanded
         })}
      >
         <div className='shrink-0'>
            <ClockFastBackwardIcon
               height={24}
               width={24}
               fill={themes.chatHistoryIconColor}
            />
         </div>
         <p
            className={cn(
               Styles.headingTextClassName,
               themes.chatHistoryHeadingTextColor
            )}
         >
            {chatBoxT('chatHistory')}
         </p>
      </div>
   )

   const renderSearchIcon = (): React.ReactElement => (
      <div className='mr-2'>
         <SearchMdIcon width={18} height={18} fill={themes.searchIconColor} />
      </div>
   )

   const renderSearchInput = (): React.ReactElement => (
      <TextField
         size={'ExtraSmall'}
         placeholder={t('search')}
         title={searchText}
         value={searchText}
         containerClassName={'w-full'}
         leftElement={renderSearchIcon}
         onChange={setSearchText}
      />
   )

   const renderHeaderActions = (): React.ReactElement => (
      <div className={Styles.headerActionsContainerClassName}>
         <div
            className={Styles.headerActionContainerClassName}
            onClick={onClickNewThread}
         >
            <PlusIcon width={24} height={24} fill={themes.plusIconColor} />
         </div>
         <div
            className={Styles.headerActionContainerClassName}
            onClick={isMobileDevice ? onClickBack : onClose}
         >
            <XCloseIcon
               width={24}
               height={24}
               fill={themes.chatBoxCloseIconFillColor}
            />
         </div>
      </div>
   )

   const renderLeftIcon = (): React.ReactElement => (
      <ArrowNarrowLeftIcon
         width={20}
         height={20}
         stroke={themes.backIconColor}
      />
   )

   const renderSidepeekHeaderActions = (): React.ReactElement => (
      <div className='flex items-center gap-x-2'>
         <IconButton
            hierarchy={IconButtonHierarchy.Tertiary}
            size={'Small'}
            subVariant={IconButtonSubVariant.Gray}
            icon={() => (
               <ChevronLeftDoubleIcon
                  height={20}
                  width={20}
                  fill={themes.chevronLeftDoubleIconColor}
               />
            )}
            onClick={() => {
               setShouldShowChatHistoryInSidepeek(false)
            }}
         />
         {aiChatUIStore.activeUIStateEnum !==
         ChatConfigUIStateEnum.SearchState ? (
            <IconButton
               hierarchy={IconButtonHierarchy.Tertiary}
               size={'Small'}
               subVariant={IconButtonSubVariant.Gray}
               icon={() => (
                  <PlusIcon
                     height={20}
                     width={20}
                     fill={themes.plusIconColor}
                  />
               )}
               onClick={onClickNewThread}
            />
         ) : null}
      </div>
   )

   const renderHeader = (): React.ReactElement => (
      <div className={Styles.headerContainerClassName}>
         <div className='flex items-center'>
            {!isMobileDevice && !isChatBoxExpanded ? (
               <Button
                  size={'Small'}
                  subVariant={SubVariant.Gray}
                  hierarchy={Hierarchy.Link}
                  leftIcon={renderLeftIcon}
                  onClick={onClickBack}
               >
                  {t('back')}
               </Button>
            ) : null}
            {isMobileDevice || isChatBoxExpanded
               ? renderChatHistoryHeading()
               : null}
         </div>

         <div>
            {isChatBoxExpanded
               ? renderSidepeekHeaderActions()
               : renderHeaderActions()}
         </div>
      </div>
   )

   return (
      <div className={Styles.containerClassName}>
         {renderHeader()}

         <div className={Styles.bodyContainerClassName}>
            {/* //TODO: enable search after discussing changes with backend */}
            {/* <div className='my-4 px-[20px]'>{renderSearchInput()}</div> */}
            <div
               id={CHAT_HISTORY_LIST_SCROLL_TARGET_ID}
               className={Styles.chatHistoryListContainerClassName}
            >
               {!isMobileDevice && !isChatBoxExpanded
                  ? renderChatHistoryHeading()
                  : null}

               {renderChatHistoryList()}
            </div>
         </div>
      </div>
   )
}

export default observer(ChatHistoryController)
